import * as React from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  FloatingPortal,
  useDismiss,
  useInteractions,
  arrow,
  FloatingArrow,
} from '@floating-ui/react'
import { Editor } from '@tiptap/react'
import { MiniTopic } from '@deepguide-ai/types'
import { Trash2, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DocIcon } from '@/components/ui/doc-icon'
import { LinkEditForm } from './link-edit-form'
import './link-floating-editor.scss'
import { useLinkHandler } from './link-popover'
import { PageSearch } from './page-search'

interface LinkFloatingEditorProps {
  editor: Editor
  isOpen: boolean
  onClose: () => void
  initialMode?: 'edit' | 'view' | 'search'
  cursorPosition?: { from: number; to: number } | null
  slashCommandRange?: { from: number; to: number } | null
}

interface LinkViewProps {
  url: string
  title?: string
  topic?: MiniTopic | null
  onEdit: () => void
  onRemove: () => void
  onUpdateTitle: (newTitle: string) => void
}

function LinkView({
  url,
  title,
  topic,
  onEdit,
  onRemove,
  onUpdateTitle,
}: LinkViewProps) {
  const [isEditingTitle, setIsEditingTitle] = React.useState(false)
  const [editedTitle, setEditedTitle] = React.useState(title || '')
  const titleInputRef = React.useRef<HTMLInputElement>(null)
  const updateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  React.useEffect(() => {
    setEditedTitle(title || '')
  }, [title])

  React.useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      // Set cursor at the end
      titleInputRef.current.setSelectionRange(
        titleInputRef.current.value.length,
        titleInputRef.current.value.length
      )
    }
  }, [isEditingTitle])

  // Real-time update with debouncing
  React.useEffect(() => {
    if (isEditingTitle && editedTitle !== title) {
      // Clear existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Set new timeout for real-time update
      updateTimeoutRef.current = setTimeout(() => {
        onUpdateTitle(editedTitle)
      }, 100) // Reduced to 100ms for smoother updates
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [editedTitle, isEditingTitle, title, onUpdateTitle])

  const handleTitleSave = () => {
    // Clear timeout to prevent double update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current)
    }

    if (editedTitle !== title) {
      onUpdateTitle(editedTitle)
    }
    setIsEditingTitle(false)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleTitleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      // Clear timeout to prevent update
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
      setEditedTitle(title || '')
      setIsEditingTitle(false)
    }
  }

  const displayUrl = topic?.title || url
  const isInternal = !!topic

  return (
    <div className='link-view'>
      <div className='link-view-field' onClick={() => onEdit()}>
        <div className='link-view-label'>Page or URL</div>
        <div className='link-view-value'>
          <div className='link-view-info'>
            {isInternal && topic ? (
              <DocIcon topic={topic} />
            ) : (
              <Globe className='link-view-icon' />
            )}
            <span className='link-view-url'>{displayUrl}</span>
          </div>
        </div>
      </div>

      <div className='link-view-field'>
        <div className='link-view-label'>Link title</div>
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            type='text'
            value={editedTitle}
            onChange={(e) => setEditedTitle(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={handleTitleKeyDown}
            className='link-view-title-input  link-view-title-input--editing'
            autoComplete='off'
          />
        ) : (
          <div
            className='link-view-title-display'
            onClick={() => setIsEditingTitle(true)}
          >
            {editedTitle || title || 'Click to add title'}
          </div>
        )}
      </div>

      <div className='link-view-footer border-t'>
        <Button onClick={onRemove} className='link-view-remove !text-destructive hover:!bg-destructive/10' variant='ghost'>
          {/* <Trash2 className='h-4 w-4' /> */}
          Remove link
        </Button>
      </div>
    </div>
  )
}

export function LinkFloatingEditor({
  editor,
  isOpen,
  onClose,
  initialMode,
  cursorPosition,
  slashCommandRange,
}: LinkFloatingEditorProps) {
  const [mode, setMode] = React.useState<'edit' | 'view' | 'search'>('view')
  const [searchQuery, setSearchQuery] = React.useState('')
  // The Topic model is gone; the internal-page-link picker has nothing to offer.
  const allTopicsFlat: any[] = []
  const arrowRef = React.useRef(null)
  const selectionRef = React.useRef<{ from: number; to: number } | null>(null)
  const editingLinkIdRef = React.useRef<string | null>(null)
  const [selectedDocId, setSelectedDocId] = React.useState<string | undefined>(
    undefined
  )
  const [editingLinkHref, setEditingLinkHref] = React.useState<string>("")

  // Check if we're editing an existing link
  const isEditingExistingLink = React.useMemo(() => {
    if (!editor) return false
    const { from, to } = editor.state.selection
    const text = editor.state.doc.textBetween(from, to, ' ')
    return editor.isActive('link') && text.length > 0
  }, [editor])

  // Mark link as editing
  const markLinkAsEditing = React.useCallback(
    (from: number, to: number) => {
      // Generate a unique ID for this editing session
      const editingId = `editing-${Date.now()}`
      editingLinkIdRef.current = editingId

      // Store the original position for this editing session
      selectionRef.current = { from, to }

      // Add a data attribute to mark this link as being edited
      const { state } = editor
      const { tr } = state

      // Find the link mark on text nodes within the range
      let linkMark: any = null
      state.doc.nodesBetween(from, to, (node, _pos) => {
        if (node.isText && node.marks.length > 0) {
          const mark = node.marks.find((m) => m.type.name === 'link')
          if (mark && !linkMark) {
            linkMark = mark
          }
        }
      })

      if (linkMark) {
        // Create a new mark with editing attribute
        const newMark = editor.schema.marks.link.create({
          href: linkMark.attrs.href,
          target: linkMark.attrs.target,
          rel: linkMark.attrs.rel,
          class: linkMark.attrs.class,
          'data-editing': editingId,
        })

        // Remove old mark and add new mark
        tr.removeMark(from, to, linkMark.type)
        tr.addMark(from, to, newMark)

        editor.view.dispatch(tr)

        // Track the href for this editing session so display doesn't jump to other links
        setEditingLinkHref(linkMark.attrs.href || '')
      }

      return editingId
    },
    [editor]
  )

  // Remove editing mark
  const removeEditingMark = React.useCallback(() => {
    if (!editingLinkIdRef.current) return

    const editingId = editingLinkIdRef.current
    editor.view.state.doc.descendants((node, pos) => {
      if (node.isText && node.marks.length > 0) {
        const linkMark = node.marks.find(
          (mark) =>
            mark.type.name === 'link' &&
            mark.attrs['data-editing'] === editingId
        )
        if (linkMark) {
          // Remove the editing attribute
          const newMark = editor.schema.marks.link.create({
            href: linkMark.attrs.href,
            target: linkMark.attrs.target,
            rel: linkMark.attrs.rel,
            class: linkMark.attrs.class,
          })
          editor
            .chain()
            .command(({ tr }) => {
              tr.removeMark(pos, pos + node.nodeSize, linkMark)
              tr.addMark(pos, pos + node.nodeSize, newMark)
              return true
            })
            .run()
        }
      }
    })

    editingLinkIdRef.current = null
  }, [editor])

  React.useEffect(() => {
    if (isOpen) {
      // Small delay to ensure selection is properly set if coming from hover card
      const checkAndMarkLink = () => {
        // Use provided cursor position or current selection
        if (cursorPosition) {
          selectionRef.current = cursorPosition
        } else {
          // Store the current selection when opening
          const { from, to } = editor.state.selection
          selectionRef.current = { from, to }
        }
        // Define from and to for use below
        const { from, to } = selectionRef.current || editor.state.selection
        // Find and mark the link at this position
        if (editor.isActive('link')) {
          // Get the first text node in the selection to check for link marks
          let linkMark: any = null
          let linkStart = from
          let linkEnd = to
          // Check for link marks on nodes within the selection
          editor.state.doc.nodesBetween(from, to, (node, pos) => {
            if (node.isText && node.marks.length > 0 && !linkMark) {
              const mark = node.marks.find((m) => m.type.name === 'link')
              if (mark) {
                linkMark = mark
                // Find the full extent of this link
                linkStart = pos
                linkEnd = pos + node.nodeSize
                // Scan backwards to find start of link
                while (linkStart > 0) {
                  const nodeBefore = editor.state.doc.nodeAt(linkStart - 1)
                  if (
                    nodeBefore &&
                    nodeBefore.isText &&
                    nodeBefore.marks.some(
                      (m) =>
                        m.type.name === 'link' &&
                        m.attrs.href === mark.attrs.href
                    )
                  ) {
                    linkStart--
                  } else {
                    break
                  }
                }
                // Scan forwards to find end of link
                let scanPos = linkEnd
                while (scanPos < editor.state.doc.content.size) {
                  const nodeAfter = editor.state.doc.nodeAt(scanPos)
                  if (
                    nodeAfter &&
                    nodeAfter.isText &&
                    nodeAfter.marks.some(
                      (m) =>
                        m.type.name === 'link' &&
                        m.attrs.href === mark.attrs.href
                    )
                  ) {
                    linkEnd = scanPos + nodeAfter.nodeSize
                    scanPos = linkEnd
                  } else {
                    break
                  }
                }
                return false // Stop searching once we found the link
              }
            }
          })
          if (linkMark) {
            // Mark this link as being edited
            markLinkAsEditing(linkStart, linkEnd)
          }
        }
      }

      // Check immediately and after a small delay
      checkAndMarkLink()
      setTimeout(checkAndMarkLink, 100)
    } else {
      // Remove editing mark when closing
      removeEditingMark()
    }

    if (initialMode) {
      // Use the provided initial mode
      setMode(initialMode)
    } else {
      // Start in view mode if editing existing link, search mode if creating new
      setMode(isEditingExistingLink ? 'view' : 'search')
    }
  }, [
    isEditingExistingLink,
    isOpen,
    initialMode,
    editor,
    markLinkAsEditing,
    removeEditingMark,
    cursorPosition,
  ])

  // Get the selection coordinates
  const getReferenceElement = React.useCallback(() => {
    if (!editor || !editor.state) return null

    try {
      // For slash commands with range, we need to create a range for the slash command text
      if (slashCommandRange) {
        const { from, to } = slashCommandRange

        // Get DOM positions for the slash command range
        const domStart = editor.view.domAtPos(from)
        const domEnd = editor.view.domAtPos(to)

        // Create a range that covers the slash command text
        const range = document.createRange()
        range.setStart(domStart.node, domStart.offset)
        range.setEnd(domEnd.node, domEnd.offset)

        const rect = range.getBoundingClientRect()
        console.log('Slash command range rect:', rect)

        return {
          getBoundingClientRect: () => rect,
          getClientRects: () => range.getClientRects(),
        }
      }

      // For other cases, use the stored position or current selection
      const position = cursorPosition || editor.state.selection
      const { from, to } = position

      const domRange = editor.view.domAtPos(from)
      const domRangeEnd = editor.view.domAtPos(to)

      // Create a range from the selection
      const range = document.createRange()
      range.setStart(domRange.node, domRange.offset)
      range.setEnd(domRangeEnd.node, domRangeEnd.offset)

      // Return a virtual element for floating-ui
      return {
        getBoundingClientRect: () => range.getBoundingClientRect(),
        getClientRects: () => range.getClientRects(),
      }
    } catch (error) {
      console.error('Error getting reference element:', error)
      // Return a fallback position at the current selection
      return {
        getBoundingClientRect: () => {
          const selection = window.getSelection()
          if (selection && selection.rangeCount > 0) {
            return selection.getRangeAt(0).getBoundingClientRect()
          }
          // Ultimate fallback - center of viewport
          return new DOMRect(window.innerWidth / 2, 100, 0, 0)
        },
        getClientRects: () => new DOMRectList(),
      }
    }
  }, [editor, cursorPosition, slashCommandRange])

  const referenceElement = React.useMemo(() => {
    if (!isOpen) return null
    const element = getReferenceElement()
    if (element) {
      const rect = element.getBoundingClientRect()
      console.log('LinkFloatingEditor reference element:', {
        rect: {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          top: rect.top,
          left: rect.left,
        },
        cursorPosition,
        slashCommandRange,
        isOpen,
        hasElement: !!element,
        editorSelection: editor?.state.selection,
      })
    }
    return element
  }, [isOpen, getReferenceElement, cursorPosition])

  const { refs, floatingStyles, context, update } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (!open) onClose()
    },
    placement: 'bottom-start',
    middleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
      }),
      shift({ padding: 5 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  })

  // Set reference element with a small delay for slash commands
  React.useEffect(() => {
    if (referenceElement) {
      refs.setReference(referenceElement as any)
      // Force position update
      if (slashCommandRange) {
        setTimeout(() => {
          update()
        }, 10)
      }
    }
  }, [referenceElement, refs, slashCommandRange, update])

  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
  })

  const { getFloatingProps } = useInteractions([dismiss])

  const linkHandler = useLinkHandler({
    editor,
    onSetLink: () => {
      setMode('view')
    },
  })
  // Parse docId from the href of the currently editing link only
  React.useEffect(() => {
    if (!isOpen) return
    const q = editingLinkHref.split('?')[1] || ''
    const params = new URLSearchParams(q)
    const id = params.get('docId') || undefined
    setSelectedDocId(id || undefined)
  }, [isOpen, editingLinkHref])

  // Find linked topic if internal link
  const linkedTopic = React.useMemo(() => {
    if (!selectedDocId) return null
    return allTopicsFlat.find((topic) => topic.docId === selectedDocId) || null
  }, [allTopicsFlat, selectedDocId])

  // Track the current link text
  const [currentLinkText, setCurrentLinkText] = React.useState('')

  // Get initial link text
  React.useEffect(() => {
    if (isOpen && editor && selectionRef.current) {
      const { from, to } = selectionRef.current
      const text = editor.state.doc.textBetween(from, to, ' ')
      setCurrentLinkText(text)
    }
  }, [isOpen, editor, cursorPosition])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      removeEditingMark()
    }
  }, [removeEditingMark])

  // Freeze current link href when面板打开，避免被其它链接的 selectionUpdate 干扰
  React.useEffect(() => {
    if (!editor) return
    if (isOpen) {
      const attrs = editor.getAttributes('link') as any
      if (attrs?.href) {
        setEditingLinkHref(attrs.href)
      }
    } else {
      // 面板关闭时重置，防止下次沿用旧值
      setEditingLinkHref('')
    }
  }, [isOpen, editor])

  // When the editor popover is open, keep the current editing link href in sync by scanning for the marked link
  React.useEffect(() => {
    if (!isOpen || !editor) return
    const editingId = editingLinkIdRef.current
    if (!editingId) return
    let foundHref: string | null = null
    editor.view.state.doc.descendants((node) => {
      if (node.isText && node.marks.length > 0) {
        const mark = node.marks.find(
          (m) => m.type.name === 'link' && m.attrs['data-editing'] === editingId
        )
        if (mark) {
          foundHref = mark.attrs.href || ''
          return false
        }
      }
      return
    })
    if (foundHref != null) {
      setEditingLinkHref(foundHref)
    }
  }, [isOpen, editor])

  if (!isOpen || !editor) {
    return null
  }

  console.log('LinkFloatingEditor rendering:', {
    floatingStyles,
    referenceElement: !!referenceElement,
    isOpen,
  })

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          visibility: referenceElement ? 'visible' : 'hidden',
        }}
        {...getFloatingProps()}
        className='link-floating-editor-container'
      >
        <FloatingArrow
          ref={arrowRef}
          context={context}
          className='link-floating-arrow'
        />
        {mode === 'search' ? (
          <PageSearch
            initialQuery={searchQuery}
            onSelect={(url, title) => {
              // Check if this is from slash command
              const isFromSlashCommand = !!slashCommandRange

              if (!isFromSlashCommand && selectionRef.current) {
                // Restore selection only for non-slash command cases
                editor
                  .chain()
                  .focus()
                  .setTextSelection(selectionRef.current)
                  .run()
              }

              // If we have selected text, just apply the link and close
              if (currentLinkText && !isFromSlashCommand) {
                linkHandler.setLink(url)
                setSelectedDocId(
                  url.split('?')[1]?.split('docId=')[1]?.split('&')[0] ||
                    undefined
                )
                onClose()
              } else if (url) {
                const linkText = title || url
                // No selected text but we have a title from topic selection
                if (isFromSlashCommand && slashCommandRange) {
                  // For slash command, delete the range and insert link
                  editor
                    .chain()
                    .focus()
                    .deleteRange(slashCommandRange)
                    .insertContent([
                      {
                        type: 'text',
                        text: linkText,
                        marks: [{ type: 'link', attrs: { href: url } }],
                      },
                      {
                        type: 'text',
                        text: ' ',
                        marks: [],
                      },
                    ])
                    .run()
                } else {
                  linkHandler.setLink(url, linkText)
                }
                onClose()
              }
            }}
            onBack={() => {
              setMode(isEditingExistingLink ? 'view' : 'edit')
            }}
          />
        ) : mode === 'edit' ? (
          <LinkEditForm
            url={linkHandler.url}
            editor={editor}
            onSave={(url, title) => {
              const isFromSlashCommand = !!slashCommandRange

              if (isFromSlashCommand && slashCommandRange) {
                // For slash command, use title or URL as text
                const linkText = title || url

                editor
                  .chain()
                  .focus()
                  .deleteRange(slashCommandRange)
                  .insertContent([
                    {
                      type: 'text',
                      text: linkText,
                      marks: [{ type: 'link', attrs: { href: url } }],
                    },
                    {
                      type: 'text',
                      text: ' ',
                      marks: [],
                    },
                  ])
                  .run()
              } else if (selectionRef.current && title) {
                // Restore the original selection before updating
                const { from, to } = selectionRef.current
                editor
                  .chain()
                  .focus()
                  .setTextSelection({ from, to })
                  .command(({ tr }) => {
                    // Delete the old text and insert new text with link
                    tr.deleteRange(from, to)
                    tr.insertText(title, from)
                    tr.addMark(
                      from,
                      from + title.length,
                      editor.schema.marks.link.create({ href: url })
                    )
                    return true
                  })
                  .run()
              } else if (selectionRef.current) {
                // Just update the link without changing text
                editor
                  .chain()
                  .focus()
                  .setTextSelection(selectionRef.current)
                  .setLink({ href: url })
                  .run()
              }

              if (isEditingExistingLink) {
                setMode('view')
              } else {
                onClose()
              }
            }}
            onRemove={() => {
              linkHandler.removeLink()
              onClose()
            }}
            onTitleChange={(newTitle) => {
              // Update the link text by finding the marked link
              if (newTitle !== currentLinkText && editingLinkIdRef.current) {
                const editingId = editingLinkIdRef.current
                let linkStart = -1
                let linkEnd = -1
                let linkMark: any = null

                // Find the link with our editing mark
                editor.state.doc.descendants((node, pos) => {
                  if (node.isText && node.marks.length > 0) {
                    const mark = node.marks.find(
                      (m) =>
                        m.type.name === 'link' &&
                        m.attrs['data-editing'] === editingId
                    )
                    if (mark) {
                      if (linkStart === -1) {
                        linkStart = pos
                        linkMark = mark
                      }
                      linkEnd = pos + node.nodeSize
                    }
                  }
                })

                if (linkStart !== -1 && linkMark) {
                  // Update the text without focusing the editor
                  const { tr } = editor.state

                  // Delete old text
                  tr.deleteRange(linkStart, linkEnd)
                  // Insert new text
                  tr.insertText(newTitle, linkStart)
                  // Re-apply the link mark with editing attribute
                  const newMark = editor.schema.marks.link.create({
                    ...linkMark.attrs,
                    'data-editing': editingId,
                  })
                  tr.addMark(linkStart, linkStart + newTitle.length, newMark)

                  // Dispatch without focusing
                  editor.view.dispatch(tr)

                  // Update our tracked text
                  setCurrentLinkText(newTitle)
                }
              }
            }}
            initialTitle={currentLinkText}
          />
        ) : (
          <LinkView
            url={editingLinkHref}
            title={currentLinkText}
            topic={linkedTopic}
            onEdit={() => {
              setSearchQuery(linkedTopic?.title || editingLinkHref)
              setMode('search')
            }}
            onRemove={() => {
              // Try to remove by locating the editing-marked link range first
              console.log('remove link', editor.state.doc.content)
              const editingId = editingLinkIdRef.current
              let linkStart = -1
              let linkEnd = -1
              if (editingId) {
                editor.view.state.doc.descendants((node, pos) => {
                  if (node.isText && node.marks.length > 0) {
                    const mark = node.marks.find(
                      (m) =>
                        m.type.name === 'link' &&
                        m.attrs['data-editing'] === editingId
                    )
                    if (mark) {
                      if (linkStart === -1) {
                        linkStart = pos
                      }
                      linkEnd = pos + node.nodeSize
                    }
                  }
                })
              }

              if (linkStart !== -1 && linkEnd !== -1) {
                const { tr } = editor.state
                tr.removeMark(linkStart, linkEnd, editor.schema.marks.link)
                editor.view.dispatch(tr)
              } else {
                // Fallback: ensure selection is on the link then call unsetLink
                if (selectionRef.current) {
                  const { from, to } = selectionRef.current
                  editor
                    .chain()
                    .focus()
                    .setTextSelection({ from, to })
                    .extendMarkRange('link')
                    .unsetLink()
                    .setMeta('preventAutolink', true)
                    .run()
                } else {
                  // Last fallback: attempt unset on current selection
                  editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .unsetLink()
                    .setMeta('preventAutolink', true)
                    .run()
                }
              }
              onClose()
            }}
            onUpdateTitle={(newTitle) => {
              // Update the link text by finding the marked link
              if (newTitle !== currentLinkText && editingLinkIdRef.current) {
                const editingId = editingLinkIdRef.current
                let linkStart = -1
                let linkEnd = -1
                let linkMark: any = null

                // Find the link with our editing mark
                editor.state.doc.descendants((node, pos) => {
                  if (node.isText && node.marks.length > 0) {
                    const mark = node.marks.find(
                      (m) =>
                        m.type.name === 'link' &&
                        m.attrs['data-editing'] === editingId
                    )
                    if (mark) {
                      if (linkStart === -1) {
                        linkStart = pos
                        linkMark = mark
                      }
                      linkEnd = pos + node.nodeSize
                    }
                  }
                })

                if (linkStart !== -1 && linkMark) {
                  // Update the text without focusing the editor
                  const { tr } = editor.state

                  // Delete old text
                  tr.deleteRange(linkStart, linkEnd)
                  // Insert new text
                  tr.insertText(newTitle, linkStart)
                  // Re-apply the link mark with editing attribute
                  const newMark = editor.schema.marks.link.create({
                    ...linkMark.attrs,
                    'data-editing': editingId,
                  })
                  tr.addMark(linkStart, linkStart + newTitle.length, newMark)

                  // Dispatch without focusing
                  editor.view.dispatch(tr)

                  // Update our tracked text
                  setCurrentLinkText(newTitle)
                }
              }
            }}
          />
        )}
      </div>
    </FloatingPortal>
  )
}
